import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
// api
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
// constant
import { DASHBOARD_CONSTANTS } from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type { TrafficCongestionByLocationParams } from '../types/dashboard.types';

// Shape SmartCityMaps' heatmap layer expects.
export interface CongestionHeatPoint {
  id: string;
  lat: number;
  lng: number;
  // 0-1, from congestion_pct - the heatmap layer's weight.
  weight: number;
}

// Per-CCTV congestion % for today, for the traffic dashboard map's heatmap layer.
export const useTrafficCongestionByLocation = (params: TrafficCongestionByLocationParams = {}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trafficCongestionByLocation', params],
    queryFn: () => dashboardApi.getTrafficCongestionByLocation(params),
    staleTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.STALE_TIME_MS,
    gcTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.CACHE_TIME_MS,
    refetchInterval: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.REFETCH_INTERVAL_MS,
    placeholderData: (prev) => prev,
  });

  const locations = data?.data?.locations;

  // Stable reference across renders that don't carry new query data - the map
  // layer's effect keys off this array, and rebuilding its WebGL heatmap layer
  // on every unrelated re-render (not just on real data changes) races the
  // layer's async buffer build against its own teardown.
  const heatPoints = useMemo<CongestionHeatPoint[]>(
    () =>
      (locations ?? []).map((loc) => ({
        id: `cctv-${loc.cctv_id}`,
        lat: loc.lat,
        lng: loc.lng,
        weight: Math.max(0, Math.min(1, loc.congestion_pct / 100)),
      })),
    [locations]
  );

  return { heatPoints, isLoading, isError };
};
